import React from "react";
import { useSelector } from "react-redux";
import { Bar } from "react-chartjs-2";
import { ChartData } from "chart.js";
import { useTranslation } from "react-i18next";

import { RootState } from "../../../../../store/store";
import ChartLegend from "../../ChartLegend/ChartLegend";
import { verticalChartOptions } from "../../../../../utils/charts/chartOptions";
import theme from "../../../../../components/styles/theme/theme";
import { isRegion } from "../../../../../config";

const EmploymentValueChainStageChart: React.FC = () => {
  const { t } = useTranslation();
  const {
    statistics: { statisticsForSelectedCountry },
  } = useSelector((state: RootState) => state);

  // Round to nearest 100 for display
  const roundJob = (v: number | undefined | null) => {
    if (v == null) return null;
    return Math.round(v / 100) * 100;
  };

  const evcl = statisticsForSelectedCountry?.employment_valueChainLink;
  const downstreamInduced = roundJob(evcl?.downstream_induced);
  const hasDownstreamInduced = isRegion.usa && downstreamInduced != null && downstreamInduced > 0;

  const statisticsDataArray = [
    [null, evcl?.theSystem_theCocaColaSystem != null ? Math.round(evcl.theSystem_theCocaColaSystem) : null, null],
    [null, null, roundJob(evcl?.downstream_suppliers_and_outlets)],
    [roundJob(evcl?.upstream_suppliers_and_outlets), null, null],
    [null, null, roundJob(evcl?.downstream_suppliers_and_outlets_suppliers)],
    [roundJob(evcl?.upstream_suppliers_and_outlets_suppliers), null, null],
    ...(hasDownstreamInduced ? [[null, null, downstreamInduced]] : []),
  ].map((row) => row.map((value) => (value === 0 ? null : value)));
// @ts-ignore
  const data: ChartData<"bar"> = {
    labels: [
      t("charts.upstream_label"),
      t("charts.system_label"),
      t("charts.downstream_label"),
    ],
    datasets: [
      {
        label: t("charts.system_label") || "",
        data: statisticsDataArray[0],
        backgroundColor: theme.palette.secondary["600"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },
      {
        label: (isRegion.usa ? t("charts.outlets_label_usa") : t("charts.outlets_label")) || "",
        data: statisticsDataArray[1],
        backgroundColor: theme.palette.primary["600"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },
      {
        label: t("charts.first_tier_suppliers_label") || "",
        data: statisticsDataArray[2],
        backgroundColor: theme.palette.secondary["200"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },
      {
        label: (isRegion.usa ? t("charts.outlets_suppliers_label_usa") : t("charts.outlets_suppliers_label")) || "",
        data: statisticsDataArray[3],
        backgroundColor: theme.palette.primary["300"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },

      {
        label: t("charts.suppliers_suppliers_label") || "",
        data: statisticsDataArray[4],
        backgroundColor: theme.palette.secondary["50"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },
      ...(hasDownstreamInduced ? [{
        label: t("charts.downstream_induced_label") || "",
        data: statisticsDataArray[5],
        backgroundColor: theme.palette.primary["100"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      }] : []),
    ],
  };

  return (
    <>
      <Bar options={verticalChartOptions("employment")} data={data} />
      <ChartLegend data={data} />
    </>
  );
};

export default EmploymentValueChainStageChart;
